/**
 * Silent WebSocket stand-in for the offline QA server.
 *
 * The app opens two socket layers on boot — Socket.IO to VITE_SOCKET_URL
 * (`src/lib/socket.ts`) and Centrifugo to VITE_PLAEE_CENTRIFUGO_URL
 * (`src/lib/realtime/plaee-client.ts`). Offline, real sockets would error-loop
 * and spam reconnects; this stub parks every connection in CONNECTING forever
 * so both clients just idle quietly (their own connect timeouts may log — that
 * is pre-existing app behavior, not a QA-mode error).
 */

type CloseHandler = ((event: Event) => void) | null;

export class QaMockWebSocket extends EventTarget {
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSING = 2;
  static readonly CLOSED = 3;

  readonly url: string;
  readonly protocol = '';
  readonly extensions = '';
  binaryType: 'blob' | 'arraybuffer' = 'blob';
  bufferedAmount = 0;
  readyState: number = QaMockWebSocket.CONNECTING;

  onopen: CloseHandler = null;
  onmessage: CloseHandler = null;
  onerror: CloseHandler = null;
  onclose: CloseHandler = null;

  constructor(url: string | URL, protocols?: string | string[]) {
    super();
    void protocols; // accepted for WebSocket signature parity, ignored
    this.url = typeof url === 'string' ? url : url.href;
  }

  /** Swallow silently — there is nobody on the other end. */
  send(data: unknown): void {
    void data;
  }

  close(code?: number, reason?: string): void {
    void code;
    void reason;
    if (this.readyState === QaMockWebSocket.CLOSED) return;
    this.readyState = QaMockWebSocket.CLOSED;
    const event = new Event('close');
    this.onclose?.(event);
    this.dispatchEvent(event);
  }
}
